import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Loader2, AlertCircle, Check, ArrowRight,
  Package, MapPin, Calendar, MessageCircle,
} from 'lucide-react';
import SEO from '../components/SEO';
import RfqTrackingShare from '../components/RfqTrackingShare';
import QuotesGrid from '../components/QuotesGrid';
import { rfqTracking } from '../lib/dataClient';
import {
  RFQ_STATUS_META, indexOfStatus, isTerminal, RFQ_STATUS_ORDER,
} from '../lib/rfqStatus';
import { track, EVENTS } from '../lib/analytics';

/**
 * /track
 *
 * Public, no-auth RFQ status lookup. Buyers paste their `RFQ-XXXXXXXX`
 * reference code and get an instant view of the current status + the
 * lifecycle timeline. Powered by the `get_rfq_tracking()` SECURITY DEFINER
 * RPC so we never expose `rfqs` to anon SELECT.
 *
 * Buyer email and notes are deliberately NOT rendered on this page —
 * the buyer-private view lives at /rfq/:reference_code which uses the
 * same RPC but renders the full payload.
 *
 * SEO: noindex (no value to crawlers).
 */

const REF_PATTERN = /^RFQ-[A-Z0-9]{8}$/;
const REF_INPUT_PATTERN = /^RFQ-[A-Z0-9]{0,8}$/;

const normalize = (raw) => {
  if (!raw) return '';
  let v = raw.trim().toUpperCase().replace(/\s+/g, '');
  // Auto-prefix if the buyer pastes just the 8-char code
  if (/^[A-Z0-9]{8}$/.test(v)) v = `RFQ-${v}`;
  // Auto-insert hyphen if buyer typed "RFQ12345678"
  if (/^RFQ[A-Z0-9]{8}$/.test(v)) v = `RFQ-${v.slice(3)}`;
  return v;
};

const formatDate = (iso) =>
  !iso ? '' : new Date(iso).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });

const ACTOR_STYLES = {
  system:   { label: 'Automated',     dot: 'bg-neutral-400', tag: 'border-hairline bg-[rgba(200,166,74,0.05)] text-platinum-2' },
  admin:    { label: 'Sourcing desk', dot: 'bg-emerald-500', tag: 'border-gold-soft bg-[rgba(200,166,74,0.10)] text-gold' },
  supplier: { label: 'Supplier',      dot: 'bg-amber-500',   tag: 'border-amber-500/60 bg-amber-500/10 text-amber-300' },
  buyer:    { label: 'You',           dot: 'bg-blue-500',    tag: 'border-blue-soft bg-blue-500/10 text-blue' },
};

const StatusBadge = ({ status }) => {
  const meta = RFQ_STATUS_META[status] || {};
  const terminal = isTerminal(status);
  return (
    <span
      data-testid="track-current-status"
      className={`inline-flex items-center gap-2 border px-3 py-1 font-mono-data text-[10px] uppercase tracking-wider ${
        terminal
          ? 'border-hairline bg-[rgba(255,255,255,0.03)] text-platinum-2'
          : 'border-gold-soft bg-[rgba(200,166,74,0.10)] text-gold'
      }`}
    >
      <span className={`block h-1.5 w-1.5 rounded-full ${terminal ? 'bg-muted-warm' : 'bg-emerald-400'}`} />
      {meta.short || status}
    </span>
  );
};

const TrackRfq = () => {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const [input, setInput] = useState(params.get('ref') || '');
  const [reference, setReference] = useState('');   // last submitted ref
  const [result, setResult] = useState(null);       // { rfq, events } | null
  const [state, setState] = useState('idle');       // idle | invalid | loading | found | notfound | error
  const [errorMsg, setErrorMsg] = useState('');

  // Deep-link support: /track?ref=RFQ-XXXXXXXX auto-runs the lookup.
  useEffect(() => {
    const ref = params.get('ref');
    if (ref && REF_PATTERN.test(normalize(ref))) {
      runLookup(normalize(ref), { fromDeepLink: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runLookup = async (refCode, { fromDeepLink = false } = {}) => {
    if (!REF_PATTERN.test(refCode)) {
      setState('invalid');
      return;
    }
    setReference(refCode);
    setState('loading');
    setErrorMsg('');
    setParams({ ref: refCode }, { replace: true });

    track(EVENTS.RFQ_TRACK_LOOKUP, { reference_code: refCode, source: fromDeepLink ? 'deeplink' : 'form' });

    try {
      const rfq = await rfqTracking.getByReference(refCode);
      if (!rfq) {
        setResult(null);
        setState('notfound');
        return;
      }
      const events = await rfqTracking.listStatusEvents(refCode);
      setResult({ rfq, events: [...events].sort((a, b) => new Date(a.created_at) - new Date(b.created_at)) });
      setState('found');
    } catch (e) {
      console.error('[track]', e);
      setErrorMsg(e?.message || 'Lookup failed.');
      setState('error');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const norm = normalize(input);
    setInput(norm);
    runLookup(norm);
  };

  const handleInputChange = (e) => {
    const v = e.target.value.toUpperCase();
    // Soft-validate while typing — accept anything that's still on track to match
    if (v === '' || REF_INPUT_PATTERN.test(v) || /^[A-Z0-9-]*$/.test(v)) {
      setInput(v);
      if (state === 'invalid') setState('idle');
    }
  };

  return (
    <div data-testid="track-page" className="surface-obsidian">
      <SEO
        title="Track an RFQ"
        description="Look up the current status of your GIWAHS sourcing request with your reference code."
        noindex
      />

      {/* ─── HEADER ─────────────────────────────────────────────────── */}
      <section className="border-b border-hairline">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <div className="grid items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <span className="label-overline text-gold">/ Sourcing · Tracking</span>
              <h1
                className="mt-4 font-display tracking-tighter text-platinum"
                style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5.5vw, 4.5rem)', lineHeight: 0.95 }}
              >
                Track your RFQ.
              </h1>
              <p className="mt-5 max-w-xl text-base text-platinum-2 md:text-lg">
                Paste the <span className="font-mono-data text-gold">RFQ-XXXXXXXX</span> reference you received
                when you filed the request. We&rsquo;ll show you exactly where the sourcing desk is in the process,
                without an account.
              </p>
            </div>

            {/* Lookup form */}
            <form
              onSubmit={handleSubmit}
              data-testid="track-lookup-form"
              className="giwahs-auth-panel p-5 md:col-span-5 md:p-7"
            >
              <label htmlFor="track-ref-input" className="label-overline text-gold">Reference code</label>
              <div className="mt-2 flex items-stretch border border-hairline bg-[#0B0B0D] focus-within:border-gold-soft transition-colors">
                <input
                  ref={inputRef}
                  id="track-ref-input"
                  data-testid="track-ref-input"
                  type="text"
                  value={input}
                  onChange={handleInputChange}
                  placeholder="RFQ-A1B2C3D4"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  className="flex-1 bg-transparent px-3 py-3 font-mono-data text-sm tracking-wider text-platinum outline-none placeholder:text-muted-warm"
                  aria-invalid={state === 'invalid'}
                  aria-describedby={state === 'invalid' ? 'track-input-error' : undefined}
                />
                <button
                  type="submit"
                  data-testid="track-submit-btn"
                  disabled={state === 'loading'}
                  className="inline-flex items-center gap-2 border-l border-hairline bg-[rgba(200,166,74,0.12)] px-4 py-3 font-mono-data text-xs font-medium uppercase tracking-wider text-gold transition-colors hover:bg-[rgba(200,166,74,0.22)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {state === 'loading'
                    ? <><Loader2 size={14} strokeWidth={1.5} className="animate-spin" /> Looking up</>
                    : <><Search size={14} strokeWidth={1.5} /> Track</>}
                </button>
              </div>

              {state === 'invalid' && (
                <p id="track-input-error" data-testid="track-error-invalid" role="alert" aria-live="polite" className="giwahs-auth-error mt-3 flex items-start gap-2 px-3 py-2 text-xs">
                  <AlertCircle size={14} strokeWidth={1.5} className="mt-0.5 shrink-0" />
                  <span>
                    That doesn&rsquo;t look like a GIWAHS reference. The format is
                    {' '}<span className="font-mono-data text-gold">RFQ-</span> followed by 8 letters/digits.
                  </span>
                </p>
              )}

              <p className="mt-3 text-xs text-muted-warm">
                Lost your code? <Link to="/contact?topic=rfq" className="underline underline-offset-2 text-platinum-2 hover:text-gold transition-colors">Email the sourcing desk</Link>.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* ─── RESULT SECTION ─────────────────────────────────────────── */}
      <section className="border-b border-hairline surface-obsidian-2">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">

          {/* Idle empty hint */}
          {state === 'idle' && (
            <div data-testid="track-idle" className="grid items-stretch gap-6 md:grid-cols-3">
              {[
                { k: 1, h: 'Find your code', p: 'It was on the confirmation screen and in any email follow-up from the sourcing desk.' },
                { k: 2, h: 'Paste it above', p: 'No account, no password — the code itself is the proof you filed the request.' },
                { k: 3, h: 'See the lifecycle', p: 'Eight states from Submitted to Buyer Review with timestamped notes from our team.' },
              ].map(({ k, h, p }) => (
                <div key={k} className="giwahs-auth-panel p-6">
                  <span className="label-overline text-gold">Step {k}</span>
                  <h3 className="mt-2 font-display text-xl text-platinum">{h}</h3>
                  <p className="mt-3 text-sm text-platinum-2">{p}</p>
                </div>
              ))}
            </div>
          )}

          {/* Loading */}
          {state === 'loading' && (
            <div data-testid="track-loading" className="flex min-h-[40vh] items-center justify-center">
              <div className="text-center">
                <Loader2 size={20} strokeWidth={1.5} className="mx-auto animate-spin text-gold" />
                <div className="mt-3 label-overline text-platinum-2">Looking up {reference}</div>
              </div>
            </div>
          )}

          {/* Not found */}
          {state === 'notfound' && (
            <div data-testid="track-not-found" className="mx-auto max-w-2xl text-center">
              <AlertCircle size={20} strokeWidth={1.5} className="mx-auto text-gold" />
              <span className="mt-6 inline-block label-overline text-gold">No match</span>
              <h2 className="mt-3 font-display text-3xl tracking-tight text-platinum md:text-4xl">
                We couldn&rsquo;t find that RFQ.
              </h2>
              <p className="mt-5 text-platinum-2">
                Nothing in our system matches <span className="font-mono-data text-gold">{reference}</span>.
                The code might be mistyped, or your request may have been removed.
              </p>
              <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/rfq" data-testid="track-cta-new-rfq" className="btn-giwahs btn-giwahs-secondary">
                  Submit a new RFQ
                </Link>
                <Link to="/contact?topic=rfq" data-testid="track-cta-contact" className="btn-giwahs btn-giwahs-primary">
                  Contact support
                </Link>
              </div>
            </div>
          )}

          {/* Error */}
          {state === 'error' && (
            <div data-testid="track-error" role="alert" aria-live="polite" className="giwahs-auth-error mx-auto max-w-2xl p-6 text-center">
              <span className="label-overline">Lookup error</span>
              <p className="mt-3 text-sm">{errorMsg || 'Something went wrong. Please try again.'}</p>
              <button
                onClick={() => runLookup(reference)}
                data-testid="track-retry-btn"
                className="mt-5 border border-gold-soft px-4 py-2 font-mono-data text-xs font-medium uppercase tracking-wider text-gold transition-colors hover:bg-[rgba(200,166,74,0.10)]"
              >
                Retry
              </button>
            </div>
          )}

          {/* Found */}
          {state === 'found' && result?.rfq && (
            <FoundView rfq={result.rfq} events={result.events} navigate={navigate} />
          )}
        </div>
      </section>

      {/* ─── FOOTER CTAs ────────────────────────────────────────────── */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <div className="grid gap-10 md:grid-cols-12">
            <div className="md:col-span-7">
              <span className="label-overline text-white/60">Need more?</span>
              <h2 className="mt-3 font-display tracking-tighter" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1 }}>
                Start a new sourcing request, or browse the directory.
              </h2>
            </div>
            <div className="md:col-span-5 flex flex-wrap items-end gap-3">
              <Link to="/rfq" data-testid="track-footer-cta-rfq" className="inline-flex items-center gap-2 border border-white bg-white px-5 py-3 text-xs font-medium uppercase tracking-wider text-black hover:bg-transparent hover:text-white">
                File a new RFQ <ArrowRight size={12} strokeWidth={1.5} />
              </Link>
              <Link to="/factories" data-testid="track-footer-cta-factories" className="inline-flex items-center gap-2 border border-white px-5 py-3 text-xs font-medium uppercase tracking-wider hover:bg-white hover:text-black">
                Browse factories <ArrowRight size={12} strokeWidth={1.5} />
              </Link>
              <Link to="/contact" data-testid="track-footer-cta-contact" className="inline-flex items-center gap-2 px-2 py-3 text-xs font-medium uppercase tracking-wider text-white/80 hover:text-white">
                <MessageCircle size={12} strokeWidth={1.5} /> Contact the desk
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Found view — kept inline to make the page self-contained.
// Deliberately omits buyer_email + notes; this is a public-shareable view.
// ──────────────────────────────────────────────────────────────────────
const FoundView = ({ rfq, events }) => {
  const currentIdx = indexOfStatus(rfq.status);
  const currentMeta = RFQ_STATUS_META[rfq.status] || {};
  const moq = rfq.target_quantity ? `${Number(rfq.target_quantity).toLocaleString()} units` : '—';
  const category = rfq.product_type || rfq.category || '—';
  const region = rfq.preferred_countries?.length
    ? rfq.preferred_countries.join(', ')
    : rfq.shipping_destination || '—';

  return (
    <div data-testid="track-result" className="grid gap-10 md:grid-cols-12">
      {/* Header card */}
      <div className="md:col-span-12">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-hairline pb-6">
          <div>
            <span className="label-overline text-gold">Reference</span>
            <div className="mt-2 flex flex-wrap items-baseline gap-4">
              <h2
                data-testid="track-result-ref"
                className="font-display tracking-tighter text-platinum"
                style={{ fontWeight: 700, fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', lineHeight: 1 }}
              >
                {rfq.reference_code}
              </h2>
              <StatusBadge status={rfq.status} />
            </div>
            <p className="mt-3 max-w-xl text-sm text-platinum-2">{currentMeta?.description}</p>
          </div>
          <div className="giwahs-auth-panel p-4">
            <span className="label-overline text-gold">Submitted</span>
            <div data-testid="track-result-submitted" className="mt-1 font-mono-data text-sm text-platinum">{formatDate(rfq.created_at)}</div>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="md:col-span-7">
        <span className="label-overline text-gold">Lifecycle</span>
        <h3 className="mt-2 font-display text-2xl tracking-tight text-platinum">Status history.</h3>

        <ol className="mt-8 border-l border-hairline" data-testid="track-timeline">
          {RFQ_STATUS_ORDER.map((status, i) => {
            const event = events.find(e => e.status === status);
            const reached = i <= currentIdx;
            const isCurrent = i === currentIdx && !isTerminal(rfq.status);
            const meta = RFQ_STATUS_META[status];
            const actor = event?.actor || 'system';
            const actorStyle = ACTOR_STYLES[actor] || ACTOR_STYLES.system;

            return (
              <li
                key={status}
                data-testid={`track-timeline-step-${status}`}
                className={`relative -ml-px border-l-[3px] pl-6 pr-2 pb-8 ${reached ? 'border-gold-soft' : 'border-hairline'}`}
              >
                <span className={`absolute -left-[7px] top-0 grid h-3 w-3 place-items-center border ${reached ? 'border-gold-soft bg-gold' : 'border-hairline bg-[#0B0B0D]'}`} style={reached ? { backgroundColor: '#C8A64A' } : undefined}>
                  {reached && <Check size={8} strokeWidth={3} className="text-obsidian" style={{ color: '#010103' }} />}
                </span>
                <div className="flex flex-wrap items-baseline gap-3">
                  <span className={`label-overline ${reached ? 'text-gold' : 'text-muted-warm'}`}>Step {i + 1}</span>
                  <span className={`font-display text-base ${reached ? 'text-platinum' : 'text-muted-warm'}`}>{meta.label}</span>
                  {isCurrent && <span className="border border-gold-soft bg-[rgba(200,166,74,0.15)] px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider text-gold">Now</span>}
                  {event && (
                    <span
                      data-testid={`track-actor-${status}`}
                      className={`inline-flex items-center gap-1.5 border px-2 py-0.5 font-mono-data text-[10px] uppercase tracking-wider ${actorStyle.tag}`}
                    >
                      <span className={`block h-1 w-1 rounded-full ${actorStyle.dot}`} />
                      {actorStyle.label}
                    </span>
                  )}
                </div>
                <p className={`mt-2 text-sm ${reached ? 'text-platinum-2' : 'text-muted-warm'}`}>
                  {event?.note || meta.description}
                </p>
                {event?.created_at && (
                  <div className="mt-2 font-mono-data text-[10px] uppercase tracking-wider text-muted-warm">
                    {formatDate(event.created_at)}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      </div>

      {/* Summary aside */}
      <aside className="md:col-span-5 space-y-6">
        <div data-testid="track-summary" className="giwahs-auth-panel p-6">
          <span className="label-overline text-gold">RFQ summary</span>
          <h3 className="mt-2 font-display text-xl text-platinum">What you asked for.</h3>
          <dl className="mt-5 space-y-3 text-sm">
            <div className="flex items-start gap-3 border-b border-hairline pb-3">
              <Package size={14} strokeWidth={1.5} className="mt-0.5 text-gold" />
              <div className="flex-1">
                <dt className="label-overline text-muted-warm">Category</dt>
                <dd className="text-platinum" data-testid="track-summary-category">{category}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3 border-b border-hairline pb-3">
              <span className="mt-0.5 inline-block font-mono-data text-[10px] uppercase tracking-wider text-gold">MOQ</span>
              <div className="flex-1">
                <dt className="label-overline text-muted-warm">Quantity</dt>
                <dd className="text-platinum" data-testid="track-summary-moq">{moq}</dd>
              </div>
            </div>
            <div className="flex items-start gap-3 border-b border-hairline pb-3">
              <MapPin size={14} strokeWidth={1.5} className="mt-0.5 text-gold" />
              <div className="flex-1">
                <dt className="label-overline text-muted-warm">Sourcing region</dt>
                <dd className="text-platinum" data-testid="track-summary-region">{region}</dd>
              </div>
            </div>
            {rfq.needed_by_date && (
              <div className="flex items-start gap-3">
                <Calendar size={14} strokeWidth={1.5} className="mt-0.5 text-gold" />
                <div className="flex-1">
                  <dt className="label-overline text-muted-warm">Needed by</dt>
                  <dd className="text-platinum">{rfq.needed_by_date}</dd>
                </div>
              </div>
            )}
          </dl>
        </div>

        <div className="border border-gold-soft bg-[rgba(200,166,74,0.06)] p-6 text-platinum">
          <span className="label-overline text-gold">Want the full view?</span>
          <h3 className="mt-2 font-display text-xl text-platinum">Open the private dashboard.</h3>
          <p className="mt-3 text-sm text-platinum-2">
            See your full brief, attached supplier shortlist, and a Contact-the-desk shortcut at the per-RFQ tracking URL.
          </p>
          <Link
            to={`/rfq/${rfq.reference_code}`}
            data-testid="track-open-dashboard"
            className="btn-giwahs btn-giwahs-primary mt-5"
          >
            Open private dashboard <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>

        {/* Compact quotes block — same data, condensed view */}
        {Array.isArray(rfq.quotes_json) && rfq.quotes_json.length > 0 && (
          <div data-testid="track-quotes-block" className="giwahs-auth-panel p-6">
            <div className="flex items-baseline justify-between">
              <span className="label-overline text-gold">Quotes received</span>
              <span className="font-mono-data text-[10px] uppercase tracking-wider text-muted-warm">{rfq.quotes_json.length} quote{rfq.quotes_json.length === 1 ? '' : 's'}</span>
            </div>
            <QuotesGrid quotes={rfq.quotes_json} reference={rfq.reference_code} compact />
            <p className="mt-3 text-[11px] text-muted-warm">Open the private dashboard above for notes, attachments, and the full timeline.</p>
          </div>
        )}

        {/* QR + share card — encourages buyers to save the link to their phone */}
        <RfqTrackingShare reference={rfq.reference_code} compact />

        <p className="text-xs text-muted-warm">
          We only show high-level lifecycle details on this page. Sensitive fields (your contact email, internal notes)
          are kept private to your account view.
        </p>
      </aside>
    </div>
  );
};

export default TrackRfq;
